import { Box, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { ContratoFormData } from '../../../schemas/contratoSchema';
import React from 'react';
import { formatCEP } from '@utils/formatCEP';
import { formatCpfCnpj } from '@utils/formatCpfCnpj';

// Função utilitária para buscar dados do ViaCEP
async function fetchCepData(cep: string) {
  const cleanCep = cep.replace(/\D/g, '');
  if (cleanCep.length !== 8) return null;
  try {
    const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.erro) return null;
    return data;
  } catch {
    return null;
  }
}

interface InsuredDataProps {
  isSubmitting?: boolean;
}

const InsuredData = ({ isSubmitting }: InsuredDataProps) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<ContratoFormData>();
  const [cepError, setCepError] = React.useState<string | null>(null);
  const isFirstRender = React.useRef(true);

  const cepValue = useWatch({ control, name: 'cep' });

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const cleanCep = (cepValue || '').replace(/\D/g, '');

    if (cleanCep.length === 8) {
      fetchCepData(cleanCep).then((data) => {
        if (data) {
          setCepError(null);
          setValue('uf' as keyof ContratoFormData, data.uf || '');
          setValue('municipio' as keyof ContratoFormData, data.localidade || '');
          setValue('logradouro' as keyof ContratoFormData, data.logradouro || '');
          setValue('complemento' as keyof ContratoFormData, data.complemento || '');
          setValue('bairro' as keyof ContratoFormData, data.bairro || '');
        } else {
          setCepError('CEP inválido ou não encontrado');
          setValue('uf' as keyof ContratoFormData, '');
          setValue('municipio' as keyof ContratoFormData, '');
          setValue('logradouro' as keyof ContratoFormData, '');
          setValue('complemento' as keyof ContratoFormData, '');
          setValue('bairro' as keyof ContratoFormData, '');
        }
      });
    } else if (cleanCep.length < 8) {
      setCepError(null);
    }
  }, [cepValue, setValue]);

  React.useEffect(() => {
    try {
      const storage = localStorage.getItem('consultaCredito');
      if (storage) {
        const dados = JSON.parse(storage);
        setValue('email' as keyof ContratoFormData, dados.email || '');
        setValue('telefone' as keyof ContratoFormData, dados.telefone || '');
      }
    } catch {}
    // eslint-disable-next-line
  }, []);

  return (
    <Box
      sx={{
        boxShadow: '0px 4px 4px 0px #00000040',
        borderRadius: '12px',
        backgroundColor: '#FFF',
        py: { xs: '24px', md: '40px' },
        px: { xs: '24px', md: '88px' },
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 700,
          fontSize: { xs: '16px', md: '24px' },
          color: '#004AA0',
          marginBottom: { xs: '16px', md: '40px' },
        }}
      >
        Dados do Segurado / Órgão Promotor da Licitação
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { md: '88px' },
        }}
      >
        <Controller
          name="nomeSegurado"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box sx={{ mb: '40px' }}>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Nome/Razão Social do Órgão Promotor da Licitação*:
              </Typography>
              <input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${
                    errors.nomeSegurado ? '#FF0000' : '#004AA0'
                  }`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
            </Box>
          )}
        />
        <Controller
          name="cpfCnpjSegurado"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box sx={{ mb: '40px' }}>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                CNPJ do Órgão Promotor da Licitação*:
              </Typography>
              <input
                {...field}
                value={formatCpfCnpj(field.value || '')}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, '');
                  field.onChange(onlyDigits);
                }}
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${
                    errors.cpfCnpjSegurado ? '#FF0000' : '#004AA0'
                  }`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
            </Box>
          )}
        />
      </Box>
      <Box
        component="div"
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: '#808080',
          mb: '40px',
        }}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { md: '88px' },
        }}
      >
        <Controller
          name="cep"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box sx={{ mb: '40px' }}>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                CEP (do Órgão Promotor da Licitação)*:
              </Typography>
              <input
                {...field}
                value={formatCEP(field.value || '')}
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/\D/g, '');
                  field.onChange(onlyNumbers);
                }}
                maxLength={9}
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${
                    cepError || errors.cep ? '#FF0000' : '#004AA0'
                  }`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
              {cepError && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#FF0000',
                    mt: '-4px',
                  }}
                >
                  {cepError}
                </Typography>
              )}
            </Box>
          )}
        />

        <Controller
          name="uf"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box sx={{ mb: '40px' }}>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Estado (UF)*:
              </Typography>
              <input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value.toUpperCase());
                }}
                maxLength={2}
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${errors.uf ? '#FF0000' : '#004AA0'}`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
            </Box>
          )}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { md: '88px' },
        }}
      >
        <Controller
          name="logradouro"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box sx={{ mb: '40px' }}>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Endereço*:
              </Typography>
              <input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${errors.logradouro ? '#FF0000' : '#004AA0'}`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
            </Box>
          )}
        />
        <Controller
          name="numero"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box sx={{ mb: '40px' }}>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Número*:
              </Typography>
              <input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value.toUpperCase());
                }}
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${errors.numero ? '#FF0000' : '#004AA0'}`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
            </Box>
          )}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { md: '88px' },
        }}
      >
        <Controller
          name="municipio"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box sx={{ mb: '40px' }}>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Cidade*:
              </Typography>
              <input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${errors.municipio ? '#FF0000' : '#004AA0'}`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
            </Box>
          )}
        />
        <Controller
          name="bairro"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box sx={{ mb: '40px' }}>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Bairro*:
              </Typography>
              <input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${errors.municipio ? '#FF0000' : '#004AA0'}`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
            </Box>
          )}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { md: '88px' },
        }}
      >
        <Controller
          name="complemento"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box sx={{ mb: '40px' }}>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Complemento*:
              </Typography>
              <input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${errors.complemento ? '#FF0000' : '#004AA0'}`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

export default InsuredData;
